import React, { useCallback, useState } from "react";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Label from "components/Input/Label/Label";
import Placeholder from "components/Input/Placeholder/Placeholder";
import { menuItemSx, selectionSx } from "lib/muiStyle/muiStyle";
import c from "lib/style/colorVar.scss";
import HelperMessage from "components/Input/HelperMessage/HelperMessage";
import { IconButton, Stack } from "@mui/material";
import { Close } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";

/**
 * @param {Object} props
 * @param {boolean} props.controlled
 * @param {Object} props.inputRef
 * @param {string} props.label
 * @param {string} props.subLabel
 * @param {boolean} props.hideLabel
 * @param {boolean} props.mandatory
 * @param {string} props.placeholder
 * @param {string} props.noOptionsPlaceholder
 * @param {string} props.errorMessage
 * @param {boolean} props.noErrorMessage
 * @param {string | string[]} props.defaultValue
 * @param {string | string[]} props.selectionValue
 * @param {boolean} props.multiple
 * @param {Function} props.onchangeFn
 * @param {Function} props.onfocusFn
 * @param {Function} props.onblurFn
 * @param {Object} props.sxParams
 * @param {Object[]} props.options
 * @param {boolean} props.disabled
 * @param {boolean} props.readOnly
 * @param {React.CSSProperties} props.divStyle
 * @param {boolean} props.clearable
 */
const Selection = ({
  controlled,
  inputRef,
  label,
  subLabel,
  hideLabel = false,
  emptyLabel = false,
  mandatory,
  placeholder,
  noOptionsPlaceholder,
  errorMessage,
  noErrorMessage,
  defaultValue,
  selectionValue,
  multiple,
  onchangeFn,
  onfocusFn,
  onblurFn,
  sxParams = {},
  options,
  disabled,
  readOnly,
  divStyle,
  clearable,
}) => {
  const { t } = useTranslation();

  const [focus, setFocus] = useState(false);
  const [open, setOpen] = useState(false);
  const { selectionFocus, selectionError } = sxParams;

  const hasOptions = Array.isArray(options) && options.length > 0;
  const isDisabled = disabled || !hasOptions;
  const haveValue =
    (multiple && selectionValue.length > 0) || (!multiple && selectionValue);
  const canClear = clearable && haveValue && !isDisabled && !readOnly;

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const clearButton = useCallback(() => ( // useCallback is mandatory to avoid life cycle issues
    <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        const event = { target: { value: multiple ? [] : "" } };
        onchangeFn(event);
      }}
      sx={{
        mr: "4.5px"
      }}
    >
      <Close fontSize="small" />
    </IconButton>
  ), []);
  controlled
  return (
    <div style={{ position: "relative", ...divStyle }}>
      {!hideLabel && <Label label={label} subLabel={subLabel} mandatory={mandatory} empty={emptyLabel} />}
      <Stack direction="column">
        <Select
          inputRef={inputRef}
          style={isDisabled ? { background: c.disabledFieldBg } : {}}
          size={"small"}
          open={open}
          onOpen={handleOpen}
          onClose={handleClose}
          disabled={isDisabled}
          defaultValue={defaultValue}
          value={selectionValue}
          multiple={multiple}
          onChange={onchangeFn}
          onFocus={onfocusFn ?? (() => setFocus(true))}
          onBlur={onblurFn ?? (() => setFocus(false))}
          displayEmpty
          renderValue={(() => {
            if (!controlled) {
              return !!selectionValue || disabled
                ? undefined
                : () => {
                  return <Placeholder highContract={isDisabled}>{hasOptions ? placeholder : noOptionsPlaceholder ?? t(t("component.selection.noOptions"))}</Placeholder>;
                }
            } else {
              return hasOptions ? undefined : noOptionsPlaceholder ?? t(t("component.selection.noOptions"));
            }
          })()}
          sx={() =>
            selectionSx(selectionFocus ?? (readOnly ? false : focus), selectionError, isDisabled, readOnly)
          }
          MenuProps={{
            sx: {
              "& .MuiMenu-paper": {
                maxWidth: "1px",
                maxHeight: "350px",
                overflow: "auto",
                scrollbarWidth: "thin",
                scrollbarColor: "#ddd #fff",
              },
            },
          }}
          IconComponent={(() => {
            if (canClear) {
              return clearButton
            }
            return readOnly ? null : ArrowDropDownIcon
          })()}
          inputProps={{
            ...readOnly && { readOnly: true },
          }}
        >
          {options.map((option) => {
            return (
              <MenuItem
                key={option.key}
                value={option.value}
                sx={() => menuItemSx()}
              >
                {option.title}
              </MenuItem>
            );
          })}
        </Select>
        {/* {canClear &&
          <Stack
            className={s["clear-button-container"]}
            height="42px" //Height of the select with value
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            onClick={handleOpen}
            sx={{
              position: "absolute",
              inset: 0,
              margin: !!hideLabel === !!noErrorMessage ? "auto" :
                !!hideLabel ? "0 0 auto" : "auto 0 0",
              cursor: "pointer",
            }}>
            <IconButton
              className={s["clear-button"]}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                const event = { target: { value: multiple ? [] : "" } };
                onchangeFn(event);
              }}
              sx={{
                visibility: "hidden",
                pointerEvents: "auto",
                mr: "4.5px"
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Stack>
        } */}
      </Stack>

      {!noErrorMessage && (
        <HelperMessage message={selectionError && errorMessage} />
      )}
    </div>
  );
};

export default React.memo(Selection);
